/**
 * Application-wide notification service — the SINGLE source of truth for every
 * save / success / error / warning / info message in Alpaca Intelligence
 * (CLAUDE.md §18.6, BO UI Standard §13).  No form rolls its own message box,
 * banner, toast, or `window.alert` — they all call `useNotify()`.
 *
 * TWO-TIER MESSAGE STANDARD (BO UI Standard §13, ruling 2026-07-05):
 *   • TRANSIENT (Tier 2) — routine `success` + `info` render HERE as a
 *     FULL-WIDTH SOLID top banner (legacy-XAF style), ONE at a time (a new
 *     message replaces the current one), auto-dismiss ~3.2 s.  Colours from the
 *     --ai-notify-* tokens (success green, info blue) — never the action orange.
 *   • BLOCKING (Tier 1) — `error` + `warning` are REROUTED to the shared
 *     CENTERED MODAL (dialogBus → <ConfirmProvider>): a failure or a
 *     validation/blocked-action guard must be acknowledged, so it can't be
 *     missed or scroll away.  Call sites are UNCHANGED — `notify.error(...)` /
 *     `notify.warning(...)` now open the modal automatically.  (When no
 *     provider is mounted — isolated unit tests — they fall back to the banner
 *     so nothing is lost.)
 *   • INLINE (Tier 3) — single-field validation stays next to the field
 *     (React Hook Form), never here.
 *
 * Usage:
 *   const notify = useNotify();
 *   notify.success('Saved successfully.');   // banner (never echo a new record ID)
 *   notify.error('Could not reach the server.');  // → centered error modal
 *   notify.warning('Tick at least one row.');     // → centered warning modal
 * A milestone success (ID created + detail rows + next-step actions) uses the
 * richer global `showDialog({ type: 'success', … })` instead of a banner.
 */
import {
  createContext,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { warn as warnDialog, errorDlg, dialogRunnerActive } from '../confirm/dialogBus';
import './notifications.css';

/** The four semantic severities — map 1:1 to the --ai-notify-* tokens. */
export type NotifySeverity = 'success' | 'error' | 'warning' | 'info';

export interface NotifyOptions {
  /** Override the auto-dismiss window (ms).  Omit to use the standard window. */
  durationMs?: number;
}

export interface NotifyApi {
  success: (message: string, options?: NotifyOptions) => void;
  error: (message: string, options?: NotifyOptions) => void;
  warning: (message: string, options?: NotifyOptions) => void;
  info: (message: string, options?: NotifyOptions) => void;
  /** Lower-level entry point when the severity is dynamic. */
  notify: (severity: NotifySeverity, message: string, options?: NotifyOptions) => void;
}

interface BannerItem {
  id: number;
  severity: NotifySeverity;
  message: string;
  durationMs: number;
}

/** Internal — consumed only by the `useNotify` hook. */
export const NotifyContext = createContext<NotifyApi | null>(null);

/** Tabler glyph shown in the white circle per severity. */
const ICON: Record<NotifySeverity, string> = {
  success: 'ti-check',
  error: 'ti-alert-triangle',
  warning: 'ti-info-circle',
  info: 'ti-info-circle',
};

/**
 * Standard auto-dismiss window (BO UI Standard §13):
 *   success / info ~3.2 s · error / warning ~5 s (they need longer to read).
 */
function autoDuration(severity: NotifySeverity): number {
  switch (severity) {
    case 'error':
    case 'warning':
      return 5000;
    case 'success':
    case 'info':
    default:
      return 3200;
  }
}

export function NotificationProvider({ children }: { children: ReactNode }) {
  // Single-slot: only the current banner is held; a new message replaces it.
  const [current, setCurrent] = useState<BannerItem | null>(null);
  const idRef = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The banner is its OWN row ABOVE the whole app (BO UI Standard §13, L477):
  // it must never cover the rail / top bar. We keep the bar `position:fixed` at
  // top:0 (edge-to-edge over the whole page, incl. the rail) and publish its
  // measured height as the `--ai-notify-h` CSS var; the shell consumes it to
  // push itself DOWN and shrink by exactly that height (shell.css) so the bar
  // occupies its own row and the space is reclaimed the instant it dismisses.
  const barRef = useRef<HTMLDivElement | null>(null);
  useLayoutEffect(() => {
    const root = document.documentElement;
    if (current && barRef.current) {
      root.style.setProperty('--ai-notify-h', `${barRef.current.offsetHeight}px`);
    } else {
      root.style.removeProperty('--ai-notify-h');
    }
  }, [current]);

  const clearTimer = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  const dismiss = useCallback(() => {
    clearTimer();
    setCurrent(null);
  }, [clearTimer]);

  const push = useCallback(
    (severity: NotifySeverity, message: string, options?: NotifyOptions) => {
      const text = (message ?? '').toString().trim();
      if (!text) return;
      // Tier 1 (BLOCKING) — error / warning go to the centered modal (§13
      // ruling 2026-07-05).  When a <ConfirmProvider> is mounted the bus runs
      // the modal; with none (isolated tests) the bus's fallback resolves and
      // we ALSO show a banner here so the message is never lost.
      if (severity === 'error' || severity === 'warning') {
        (severity === 'error' ? errorDlg(text) : warnDialog(text));
        // No banner in the normal (provider-mounted) case — the modal is the
        // acknowledgement.  In tests without a provider, the fall-through below
        // still shows a banner so existing behaviour/coverage holds.
        if (dialogRunnerActive()) return;
      }
      const id = ++idRef.current;
      const durationMs = options?.durationMs ?? autoDuration(severity);
      clearTimer();
      setCurrent({ id, severity, message: text, durationMs });
      timer.current = setTimeout(() => {
        // Only clear if this exact message is still showing.
        setCurrent((c) => (c && c.id === id ? null : c));
      }, durationMs);
    },
    [clearTimer],
  );

  useEffect(() => () => clearTimer(), [clearTimer]);

  const api = useMemo<NotifyApi>(
    () => ({
      success: (m, o) => push('success', m, o),
      error: (m, o) => push('error', m, o),
      warning: (m, o) => push('warning', m, o),
      info: (m, o) => push('info', m, o),
      notify: (s, m, o) => push(s, m, o),
    }),
    [push],
  );

  return (
    <NotifyContext.Provider value={api}>
      {children}
      {current && (
        <div
          key={current.id}
          ref={barRef}
          className={`ai-notify-bar ${current.severity}`}
          role="status"
          aria-live="polite"
          onClick={dismiss}
        >
          <span className="ai-notify-ic" aria-hidden="true">
            <i className={`ti ${ICON[current.severity]}`} />
          </span>
          <span className="ai-notify-msg">{current.message}</span>
        </div>
      )}
    </NotifyContext.Provider>
  );
}
